// REMIX HMR BEGIN
import * as __hmr__ from "remix:hmr";
if (import.meta) {
import.meta.hot = __hmr__.createHotContext(
//@ts-expect-error
"app/utils/utils.ts"
);
import.meta.hot.lastModified = "1773385375632.5022";
}
// REMIX HMR END

import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  };
  return date.toLocaleDateString("en-US", options);
}

export function formatTitleForTableOfContent(title: string) {
  return sanitizeURL(title, { lowercase: true, trim: true });
}

// Define a type for character mapping
type CharacterMap = { [key: string]: string };

// Define interface for sanitization options
interface SanitizeOptions {
  maxLength?: number;
  lowercase?: boolean;
  trim?: boolean;
}

/**
 * Sanitizes a string for use as a URL
 * @param input - String to sanitize
 * @param options - Configuration options
 * @returns Sanitized string
 * @throws {TypeError} If input is not a string
 * @throws {Error} If input is empty
 */
function sanitizeURL(input: string, options: SanitizeOptions = {}): string {
  // Input validation
  if (typeof input !== "string") {
    throw new TypeError("Input must be a string");
  }

  if (!input.trim()) {
    throw new Error("Input string cannot be empty");
  }

  // Default options
  const defaultOptions: Required<SanitizeOptions> = {
    maxLength: 100,
    lowercase: true,
    trim: true,
  };

  const config = { ...defaultOptions, ...options };

  // Special characters mapping
  const characterMap: CharacterMap = {
    á: "a",
    é: "e",
    í: "i",
    ó: "o",
    ú: "u",
    Á: "a",
    É: "e",
    Í: "i",
    Ó: "o",
    Ú: "u",
    ñ: "n",
    Ñ: "n",
    ü: "u",
    Ü: "u",
    à: "a",
    è: "e",
    ì: "i",
    ò: "o",
    ù: "u",
    ä: "a",
    ë: "e",
    ï: "i",
    ö: "o",
    " ": "-",
    _: "-",
  };

  let result = input;

  // Apply transformations based on options
  if (config.trim) {
    result = result.trim();
  }

  if (config.lowercase) {
    result = result.toLowerCase();
  }

  // Sanitization process
  result = result
    // Replace special characters using the map
    .replace(
      /[áéíóúáéíóúñüàèìòùäëïöü_\s]/g,
      (char) => characterMap[char] || char
    )
    // Remove non-alphanumeric characters
    .replace(/[^a-z0-9-]/g, "")
    // Remove multiple hyphens
    .replace(/-+/g, "-")
    // Remove hyphens at the start and end
    .replace(/^-|-$/g, "");

  // Apply maximum length if configured
  if (config.maxLength && result.length > config.maxLength) {
    result = result.substring(0, config.maxLength).replace(/-+$/, ""); // Remove trailing hyphens if any
  }

  return result;
}

// Type for validation results
interface ValidationResult {
  isValid: boolean;
  sanitized: string;
  errors?: string[];
}

/**
 * Validates and sanitizes a URL
 * @param input - URL to validate and sanitize
 * @returns Validation result object
 */
function validateAndSanitizeURL(input: string): ValidationResult {
  const errors: string[] = [];
  let sanitized = "";

  try {
    sanitized = sanitizeURL(input);
  } catch (error) {
    if (error instanceof Error) {
      errors.push(error.message);
    }
  }

  return {
    isValid: errors.length === 0,
    sanitized,
    errors: errors.length > 0 ? errors : undefined,
  };
}

export const getCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
  return undefined;
};

export const setCookie = (name: string, value: string, days: number): void => {
  if (typeof document === "undefined") return;
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
};
